import { useRef, type ReactNode } from 'react';
import { Caret } from './icons';

/* Os campos do ADS: linha de base que acende no foco, e o ponto de
   "lido" — que pulsa quando o ADS registra um campo recém-preenchido. */

function useCommit(value: string, onRead?: () => void) {
  const committed = useRef(value.trim().length > 0);
  const box = useRef<HTMLDivElement>(null);
  const commit = () => {
    const filled = value.trim().length > 0;
    if (filled && !committed.current && box.current) {
      const b = box.current;
      b.classList.remove('read'); void b.offsetWidth; b.classList.add('read');
      onRead?.();
    }
    committed.current = filled;
  };
  return { box, commit };
}

export function TextField({ id, label, opt, value, onChange, onRead, placeholder, type = 'text', autoComplete, maxLength, hint, invalid, spellCheck }: {
  id: string; label: ReactNode; opt?: string; value: string; onChange: (v: string) => void; onRead?: () => void;
  placeholder?: string; type?: 'text' | 'url' | 'email' | 'password'; autoComplete?: string; maxLength?: number;
  hint?: ReactNode; invalid?: boolean; spellCheck?: boolean;
}) {
  const { box, commit } = useCommit(value, onRead);
  return (
    <div className="f">
      <label htmlFor={id}>{label}{opt && <span className="opt">{opt}</span>}</label>
      <div className={`inp${value.trim() ? ' has' : ''}${invalid ? ' bad' : ''}`} ref={box}>
        <input id={id} type={type} value={value} placeholder={placeholder} autoComplete={autoComplete} maxLength={maxLength}
          aria-invalid={invalid || undefined} aria-describedby={hint ? `${id}-hint` : undefined} spellCheck={spellCheck}
          onChange={e => onChange(e.target.value)} onBlur={commit} />
      </div>
      {hint && <span className={`f-hint${invalid ? ' bad' : ''}`} id={`${id}-hint`}>{hint}</span>}
    </div>
  );
}

export function SelectField({ id, label, opt, value, options, onChange, onRead, placeholder = 'Selecione' }: {
  id: string; label: ReactNode; opt?: string; value: string; options: readonly string[];
  onChange: (v: string) => void; onRead?: () => void; placeholder?: string;
}) {
  const { box, commit } = useCommit(value, onRead);
  return (
    <div className="f">
      <label htmlFor={id}>{label}{opt && <span className="opt">{opt}</span>}</label>
      <div className={`inp${value ? ' has' : ''}`} ref={box}>
        <select id={id} className={value ? '' : 'ph'} value={value}
          onChange={e => { onChange(e.target.value); }} onBlur={commit}
          onInput={() => requestAnimationFrame(commit)}>
          <option value="">{placeholder}</option>
          {options.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
        <Caret />
      </div>
    </div>
  );
}

/** Campo sem rótulo próprio (linhas de canal). */
export function BareInput({ value, onChange, onRead, placeholder, label, type = 'url' }: {
  value: string; onChange: (v: string) => void; onRead?: () => void; placeholder?: string; label: string; type?: 'url' | 'text';
}) {
  const { box, commit } = useCommit(value, onRead);
  return (
    <div className={`inp${value.trim() ? ' has' : ''}`} ref={box}>
      <input type={type} value={value} placeholder={placeholder} aria-label={label} onChange={e => onChange(e.target.value)} onBlur={commit} />
    </div>
  );
}

export function TextArea({ id, label, value, onChange, onRead, onBlur, placeholder, maxLength, hint, minLines }: {
  id: string; label: ReactNode; value: string; onChange: (v: string) => void; onRead?: () => void; onBlur?: () => void;
  placeholder?: string; maxLength?: number; hint?: string; minLines?: number;
}) {
  const { commit } = useCommit(value, onRead);
  return (
    <div className="f">
      <label htmlFor={id}>{label}</label>
      <div className="ta">
        <textarea id={id} value={value} maxLength={maxLength} placeholder={placeholder}
          style={minLines ? { minHeight: `${minLines}lh` } : undefined}
          onChange={e => onChange(e.target.value)} onBlur={() => { commit(); onBlur?.(); }} />
        {(hint || maxLength) && (
          <div className="bar">
            <span className="hint">{hint}</span>
            {maxLength && <span className="cnt mono"><b>{value.length}</b>/{maxLength}</span>}
          </div>
        )}
      </div>
    </div>
  );
}
